/**
 * AgentPanel — de AI-telefoonassistent per nummer aan- of uitzetten.
 *
 * Praat met /api/telephony/agent-registration: dezelfde endpoint die Het Brein
 * gebruikt. Dit scherm is de demo, niet de functie (ontwerpregel: elke
 * handeling is een aanroepbare API).
 *
 * Het paneel doet activeren in één handeling: registreren bij xAI én het
 * actie-slot op `ai_agent` zetten. Los van elkaar zijn die twee stil kapot —
 * een slot zonder registratie geeft een agent die nooit opneemt (het
 * voicemail-vangnet vangt de beller), een registratie zonder slot een nummer
 * waar niemand heen wordt gestuurd. De API meldt allebei die toestanden.
 *
 * Plan: plans/2026-08-12-voice-agent-per-nummer-fase3.md
 */

import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../../lib/supabase';

interface RegistratieEvent {
  phone_number: string;
  registered_at: string;
  deaf_detected_at: string | null;
  healed_at: string | null;
  reden: string | null;
  bron: string | null;
}

interface NumberStatus {
  phone_number: string;
  brand: string;
  agent_brand: string | null;
  effectief_merk: string;
  agent_slots: string[];
  registratie: {
    modus: string;
    phone_number_id: string | null;
    webhook_id: string | null;
    heeft_secret: boolean;
  } | null;
  actief: boolean;
  waarschuwing: string | null;
  kennisbank: { eigen_artikelen: number; dun: boolean };
  doofheid_events: RegistratieEvent[];
  registratie_events: RegistratieEvent[];
}

interface StatusResponse {
  bridge: { bereikbaar: boolean; health: boolean; error?: string };
  numbers: NumberStatus[];
  brands: Array<{ code: string; naam: string }>;
}

const SLOT_LABELS: Record<string, string> = {
  in_hours_action: 'Binnen openingstijden',
  overflow_action: 'Als niemand opneemt',
  closed_action: 'Buiten openingstijden',
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

function tijd(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function AgentPanel({ phoneNumber, onChanged }: { phoneNumber: string; onChanged?: () => void }) {
  const [status, setStatus] = useState<NumberStatus | null>(null);
  const [bridge, setBridge] = useState<StatusResponse['bridge'] | null>(null);
  const [brands, setBrands] = useState<StatusResponse['brands']>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bevestig, setBevestig] = useState<'activate' | 'deactivate' | null>(null);
  const [slots, setSlots] = useState<string[]>(['closed_action']);
  const [merk, setMerk] = useState<string>('');

  const laad = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(`/api/telephony/agent-registration?number=${encodeURIComponent(phoneNumber)}`, {
        headers: await authHeaders(),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `status ${r.status}`);
      const json = (await r.json()) as StatusResponse;
      const nummer = json.numbers[0] ?? null;
      setStatus(nummer);
      setBridge(json.bridge);
      setBrands(json.brands);
      if (nummer) {
        setMerk(nummer.agent_brand ?? '');
        if (nummer.agent_slots.length > 0) setSlots(nummer.agent_slots);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber]);

  useEffect(() => {
    setIsLoading(true);
    void laad();
  }, [laad]);

  const voerUit = useCallback(
    async (action: 'activate' | 'deactivate') => {
      setBusy(true);
      setError(null);
      try {
        const r = await fetch('/api/telephony/agent-registration', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({
            phone_number: phoneNumber,
            action,
            ...(action === 'activate' ? { slots, agent_brand: merk || null } : {}),
          }),
        });
        const json = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(json.let_op ? `${json.error} — ${json.let_op}` : json.error || `status ${r.status}`);
        await laad();
        onChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
        setBevestig(null);
      }
    },
    [phoneNumber, slots, merk, laad, onChanged],
  );

  if (isLoading) {
    return <div className="text-sm text-slate-400">AI-assistent laden…</div>;
  }
  if (!status) {
    return <div className="text-sm text-rose-600">{error ?? 'Geen status voor dit nummer.'}</div>;
  }

  const merknaam = brands.find((b) => b.code === status.effectief_merk)?.naam ?? status.effectief_merk;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
            status.actief ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600',
          )}
        >
          <span className={clsx('h-1.5 w-1.5 rounded-full', status.actief ? 'bg-emerald-500' : 'bg-slate-400')} />
          {status.actief ? 'Actief' : 'Uit'}
        </span>
        <span className="text-sm text-slate-600">
          Spreekt als <strong className="text-slate-800">{merknaam}</strong>
          {status.agent_brand && status.agent_brand !== status.brand && (
            <span className="text-amber-700"> (afwijkend van het merk van dit nummer: {status.brand})</span>
          )}
        </span>
        {status.registratie && (
          <span className="text-xs text-slate-400 font-mono">
            xAI: {status.registratie.modus}
            {status.registratie.modus === 'webhook' && !status.registratie.heeft_secret && ' — GEEN SECRET'}
          </span>
        )}
      </div>

      {bridge && !bridge.bereikbaar && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          De bridge is niet bereikbaar ({bridge.error ?? 'onbekende reden'}). Activeren en deactiveren werken nu niet;
          gebruik de nood-CLI op de droplet. De routering zelf staat hier los van.
        </div>
      )}

      {status.waarschuwing && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {status.waarschuwing}
        </div>
      )}

      {status.kennisbank.dun && (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {merknaam} heeft {status.kennisbank.eigen_artikelen} eigen kennisbank-artikel(en). De agent kan inhoudelijk
          weinig beantwoorden en zal vooral terugbelverzoeken vastleggen. Dat mag, maar weet het.
        </div>
      )}

      {error && <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      {!status.actief && (
        <div className="space-y-3 rounded border border-slate-200 p-3">
          <div>
            <div className="text-sm font-medium text-slate-800">Wanneer neemt de assistent op?</div>
            <div className="mt-2 space-y-1.5">
              {Object.entries(SLOT_LABELS).map(([slot, label]) => (
                <label key={slot} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={slots.includes(slot)}
                    onChange={(e) =>
                      setSlots((s) => (e.target.checked ? [...s, slot] : s.filter((x) => x !== slot)))
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-800">Spreekt als merk</label>
            <select
              value={merk}
              onChange={(e) => setMerk(e.target.value)}
              className="mt-1 block border border-slate-200 rounded px-2 py-1.5 text-sm"
            >
              <option value="">Het merk van dit nummer ({status.brand})</option>
              {brands.map((b) => (
                <option key={b.code} value={b.code}>{b.naam}</option>
              ))}
            </select>
            <div className="mt-1 text-xs text-slate-400">
              Een ander merk kiezen is een bewuste afwijking — de beller hoort dat merk, niet dat van het nummer.
            </div>
          </div>
        </div>
      )}

      {status.actief && status.agent_slots.length > 0 && (
        <div className="text-sm text-slate-600">
          Neemt op: {status.agent_slots.map((s) => SLOT_LABELS[s] ?? s).join(', ').toLowerCase()}.
        </div>
      )}

      <div className="flex items-center gap-3">
        {status.actief ? (
          <button
            type="button"
            onClick={() => setBevestig('deactivate')}
            disabled={busy}
            className="rounded bg-white border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Assistent uitzetten
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setBevestig('activate')}
            disabled={busy || slots.length === 0 || !bridge?.bereikbaar}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Assistent aanzetten
          </button>
        )}
        <button
          type="button"
          onClick={() => void laad()}
          disabled={busy}
          className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
        >
          Vernieuwen
        </button>
      </div>

      {bevestig && (
        <div className="rounded border border-slate-300 bg-white p-3 space-y-3">
          <div className="text-sm text-slate-700">
            {bevestig === 'activate' ? (
              <>
                Dit registreert <strong>{phoneNumber}</strong> bij xAI en stuurt echte bellers naar de assistent
                ({merknaam}) op: {slots.map((s) => SLOT_LABELS[s] ?? s).join(', ').toLowerCase()}. Bel het nummer
                daarna zelf ter controle.
              </>
            ) : (
              <>
                Dit haalt de registratie van <strong>{phoneNumber}</strong> weg en zet de agent-slots terug op
                voicemail. Lopende gesprekken worden niet afgebroken.
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void voerUit(bevestig)}
              disabled={busy}
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? 'Bezig…' : 'Ja, doorvoeren'}
            </button>
            <button
              type="button"
              onClick={() => setBevestig(null)}
              disabled={busy}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {status.doofheid_events.length > 0 && (
        <div>
          <div className="text-sm font-medium text-slate-800">
            Doofheid-events (7 dagen): {status.doofheid_events.length}
          </div>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
            {status.doofheid_events.slice(0, 5).map((e, i) => (
              <li key={i}>
                {tijd(e.registered_at)} — {e.healed_at ? 'geheeld' : 'NIET geheeld'} ({e.bron ?? 'onbekend'})
                {e.reden ? `: ${e.reden}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AgentPanel;
